import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "../../lib/api";
import AddressAutocomplete, { type RuianSuggestion } from "../../components/AddressAutocomplete";

type Contact = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  street: string;
  house_number: string;
  city: string;
  postal_code: string;
  email_verified: number;
  created_at: string;
  ruian_address_id: number | null;
  latitude: number | null;
  longitude: number | null;
};

type ContactsResponse = {
  items: Contact[];
  total: number;
  page: number;
  pageSize: number;
};

type DistList = {
  id: number;
  name: string;
  member_count: number;
};

type ContactList = {
  id: number;
  name: string;
};

/* ── Modal: správa distribučních seznamů kontaktu ── */

function ListManagerModal({
  contactId,
  contactName,
  onClose,
}: {
  contactId: number;
  contactName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const backdropRef = useRef<HTMLDivElement>(null);

  // Všechny seznamy
  const { data: allLists } = useQuery({
    queryKey: ["lists"],
    queryFn: () => apiGet<{ items: DistList[] }>("/api/admin/lists"),
  });

  // Seznamy, ve kterých kontakt už je
  const { data: contactLists } = useQuery({
    queryKey: ["contact-lists", contactId],
    queryFn: () => apiGet<{ items: ContactList[] }>(`/api/admin/contacts/${contactId}/lists`),
  });

  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Inicializuj checkboxy podle aktuálního stavu
  useEffect(() => {
    if (contactLists && !initialized) {
      setChecked(new Set(contactLists.items.map((l) => l.id)));
      setInitialized(true);
    }
  }, [contactLists, initialized]);

  const originalIds = new Set(contactLists?.items.map((l) => l.id) ?? []);

  const toggle = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const toAdd = [...checked].filter((id) => !originalIds.has(id));
      const toRemove = [...originalIds].filter((id) => !checked.has(id));

      const ops: Promise<unknown>[] = [];
      for (const listId of toAdd) {
        ops.push(apiPost(`/api/admin/lists/${listId}/members`, { add: [contactId] }));
      }
      for (const listId of toRemove) {
        ops.push(apiPost(`/api/admin/lists/${listId}/members`, { remove: [contactId] }));
      }
      await Promise.all(ops);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["contact-lists", contactId] });
      onClose();
    },
  });

  const hasChanges =
    initialized &&
    ([...checked].some((id) => !originalIds.has(id)) ||
      [...originalIds].some((id) => !checked.has(id)));

  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "var(--color-bg, #fff)",
          borderRadius: 10,
          padding: 24,
          minWidth: 320,
          maxWidth: 440,
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Distribuční seznamy</h3>
        <p className="muted" style={{ fontSize: "0.9rem" }}>
          {contactName}
        </p>

        {!allLists || !initialized ? (
          <p className="muted">Načítám…</p>
        ) : allLists.items.length === 0 ? (
          <p className="muted">Zatím neexistují žádné distribuční seznamy.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allLists.items.map((l) => (
              <label
                key={l.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "var(--color-bg-hover, #f5f5f5)")}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <input
                  type="checkbox"
                  checked={checked.has(l.id)}
                  onChange={() => toggle(l.id)}
                  style={{ width: "auto", margin: 0 }}
                />
                <span>{l.name}</span>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  ({l.member_count})
                </span>
              </label>
            ))}
          </div>
        )}

        {saveMutation.isError && (
          <div className="alert alert-error" style={{ marginTop: 12, fontSize: "0.85rem" }}>
            Uložení selhalo.
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" className="secondary" onClick={onClose}>
            Zrušit
          </button>
          <button
            type="button"
            disabled={!hasChanges || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal: úprava kontaktu ── */

function EditContactModal({
  contact,
  onClose,
}: {
  contact: Contact;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const backdropRef = useRef<HTMLDivElement>(null);

  const [firstName, setFirstName] = useState(contact.first_name);
  const [lastName, setLastName] = useState(contact.last_name);
  const [phone, setPhone] = useState(contact.phone);
  const [street, setStreet] = useState(contact.street);
  const [houseNumber, setHouseNumber] = useState(contact.house_number);
  const [city, setCity] = useState(contact.city);
  const [postalCode, setPostalCode] = useState(contact.postal_code ?? "");
  const [ruianAddressId, setRuianAddressId] = useState<number | null>(contact.ruian_address_id);
  const [latitude, setLatitude] = useState<number | null>(contact.latitude);
  const [longitude, setLongitude] = useState<number | null>(contact.longitude);
  const [addressQuery, setAddressQuery] = useState(
    `${contact.street} ${contact.house_number}, ${contact.city}`.trim(),
  );
  const [manualAddress, setManualAddress] = useState(false);

  const handleSelectAddress = (s: RuianSuggestion) => {
    setStreet(s.street);
    setHouseNumber(s.houseNumber);
    setCity(s.city);
    setPostalCode(s.postalCode);
    setRuianAddressId(s.ruianId);
    setLatitude(s.latitude);
    setLongitude(s.longitude);
    setAddressQuery(s.label);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      apiPatch(`/api/admin/contacts/${contact.id}`, {
        firstName,
        lastName,
        phone,
        street,
        houseNumber,
        city,
        postalCode,
        ruianAddressId: manualAddress ? null : ruianAddressId,
        latitude,
        longitude,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
  });

  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "var(--color-bg, #fff)",
          borderRadius: 10,
          padding: 24,
          minWidth: 400,
          maxWidth: 520,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Upravit kontakt</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Jméno</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label>Příjmení</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div>
            <label>Telefon</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label>Adresa</label>
              <label style={{ fontSize: "0.85rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={manualAddress}
                  onChange={(e) => setManualAddress(e.target.checked)}
                  style={{ width: "auto", marginRight: 4 }}
                />
                Zadat ručně
              </label>
            </div>

            {manualAddress ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 2 }}>
                    <input
                      placeholder="Národní"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      placeholder="1"
                      value={houseNumber}
                      onChange={(e) => setHouseNumber(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 2 }}>
                    <input
                      placeholder="Praha"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      placeholder="110 00"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.85rem" }}>Zeměpisná šířka</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="50.0755"
                      value={latitude ?? ""}
                      onChange={(e) => setLatitude(e.target.value ? Number(e.target.value) : null)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.85rem" }}>Zeměpisná délka</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="14.4378"
                      value={longitude ?? ""}
                      onChange={(e) => setLongitude(e.target.value ? Number(e.target.value) : null)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <AddressAutocomplete
                value={addressQuery}
                onChange={setAddressQuery}
                onSelect={handleSelectAddress}
              />
            )}
          </div>
        </div>

        {saveMutation.isError && (
          <div className="alert alert-error" style={{ marginTop: 12, fontSize: "0.85rem" }}>
            Uložení selhalo.
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" className="secondary" onClick={onClose}>
            Zrušit
          </button>
          <button
            type="button"
            disabled={!firstName || !lastName || !street || !city || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Hlavní stránka kontaktů ── */

export default function AdminContacts() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [listModalContactId, setListModalContactId] = useState<number | null>(null);
  const [listModalContactName, setListModalContactName] = useState("");
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["contacts", search, page],
    queryFn: () =>
      apiGet<ContactsResponse>(
        `/api/admin/contacts?search=${encodeURIComponent(search)}&page=${page}`,
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/api/admin/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  const handleDelete = (c: Contact) => {
    if (window.confirm(`Opravdu smazat kontakt ${c.first_name} ${c.last_name}?`)) {
      deleteMutation.mutate(c.id);
    }
  };

  return (
    <div>
      <h1>Kontakty</h1>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <input
          placeholder="Hledat (jméno, email, ulice...)"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 360 }}
        />
      </div>

      {isLoading && <p>Načítám…</p>}
      {isError && <div className="alert alert-error">Nepodařilo se načíst kontakty.</div>}

      {data && (
        <>
          <p className="muted">Celkem {data.total} kontaktů</p>
          <table>
            <thead>
              <tr>
                <th>Jméno</th>
                <th>Email</th>
                <th>Telefon</th>
                <th>Adresa</th>
                <th>Stav</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.first_name} {c.last_name}
                  </td>
                  <td>{c.email}</td>
                  <td>{c.phone}</td>
                  <td>
                    {c.street} {c.house_number}, {c.city}
                  </td>
                  <td>
                    {c.email_verified ? (
                      <span style={{ color: "var(--color-success)" }}>Ověřen</span>
                    ) : (
                      <span className="muted">Čeká na ověření</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="secondary"
                      style={{ fontSize: "0.85rem", padding: "4px 10px", marginRight: 6 }}
                      onClick={() => {
                        setListModalContactId(c.id);
                        setListModalContactName(`${c.first_name} ${c.last_name}`);
                      }}
                    >
                      Distribuční seznamy
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      style={{ fontSize: "0.85rem", padding: "4px 10px", marginRight: 6 }}
                      onClick={() => setEditContact(c)}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      style={{
                        fontSize: "0.85rem",
                        padding: "4px 10px",
                        color: "var(--color-error, #d32f2f)",
                        borderColor: "var(--color-error, #d32f2f)",
                      }}
                      onClick={() => handleDelete(c)}
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: "center" }}>
                    Žádné kontakty
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {data.total > data.pageSize && (
            <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
              <button
                type="button"
                className="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Předchozí
              </button>
              <span className="muted">
                Strana {page} z {Math.ceil(data.total / data.pageSize)}
              </span>
              <button
                type="button"
                className="secondary"
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Další →
              </button>
            </div>
          )}
        </>
      )}

      {listModalContactId !== null && (
        <ListManagerModal
          contactId={listModalContactId}
          contactName={listModalContactName}
          onClose={() => setListModalContactId(null)}
        />
      )}

      {editContact !== null && (
        <EditContactModal
          contact={editContact}
          onClose={() => setEditContact(null)}
        />
      )}
    </div>
  );
}
